import { VA_FORM_IDS } from '@department-of-veterans-affairs/platform-forms/constants';
import { trackAction } from '../../../../shared/utils/tracking/datadogRumTracking';
import { isExpired } from '../../index';

// ──────────────────────────────────────────────
// Helper functions for data categorization
// ──────────────────────────────────────────────

/**
 * Categorize days remaining for BDD eligibility
 * Used to protect PII by grouping into ranges instead of exact values
 *
 * @param {number} days - Number of days remaining
 * @returns {string} Category label
 */
const categorizeDaysRemaining = days => {
  if (days <= 7) return '0-7-days';
  if (days <= 30) return '8-30-days';
  if (days <= 60) return '31-60-days';
  if (days <= 90) return '61-90-days';
  if (days <= 120) return '91-120-days';
  if (days <= 180) return '121-180-days';
  if (days <= 210) return '181-210-days';
  return '211-plus-days';
};

// ──────────────────────────────────────────────
// STEP VIEW TRACKING
// Track when users view each subtask step (not URL page changes)
// ──────────────────────────────────────────────

/**
 * Track when a subtask step is viewed
 * Also updates path history via React context
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.stepName - Name of the step being viewed
 * @param {Object} params.pathHistoryContext - Path history context from usePathHistory hook
 */
export const trackSubtaskStepView = ({ stepName, pathHistoryContext }) => {
  // Get current history BEFORE adding the step
  const history = pathHistoryContext?.getHistory?.() || {};

  // Add step to path history (this is async, so do it after getting current state)
  if (pathHistoryContext?.addStep) {
    pathHistoryContext.addStep(stepName);
  }

  trackAction('21-526EZ_subtask_stepViewed', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    stepName,

    // Path history for context (from BEFORE this step was added)
    startTime: history.startTime,
    pathSteps: history.path?.map(p => p.step) || [],
    pathTimestamps: history.path?.map(p => p.timestamp) || [],
    currentStep: history.path?.length || 0,
  });
};

// ──────────────────────────────────────────────
// IN-PROGRESS FORM TRACKING
// Track when subtask is first viewed with an existing in-progress form
// ──────────────────────────────────────────────

const hasInProgressForm = (savedForms = []) =>
  savedForms.some(
    savedForm =>
      savedForm.form === VA_FORM_IDS.FORM_21_526EZ &&
      !isExpired(savedForm.metadata),
  );

/**
 * Track when a user first views the subtask and has an in-progress form
 *
 * @param {Object} params - Tracking parameters
 * @param {Array} params.savedForms - Array of saved forms from user profile
 */
export const trackSubtaskViewedWithInProgressForm = ({ savedForms }) => {
  if (!hasInProgressForm(savedForms)) return;

  trackAction('21-526EZ_subtask_viewedWithInProgressForm', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
  });
};

// ──────────────────────────────────────────────
// USER INPUT TRACKING
// Track final selections when continuing to next page
// ──────────────────────────────────────────────

/**
 * Track active duty selection
 *
 * @param {boolean} isActiveDuty - Whether user selected they are on active duty
 */
export const trackActiveDutySelection = isActiveDuty => {
  trackAction('21-526EZ_subtask_activeDutySelected', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName: 'start',
    fieldName: 'active-duty',
    fieldValue: isActiveDuty ? 'yes' : 'no',
  });
};

/**
 * Track separation date entered (categorized for PII protection)
 *
 * @param {Object} params - Tracking parameters
 * @param {number} params.daysUntilSeparation - Number of days until separation date
 */
export const trackSeparationDateEntered = ({ daysUntilSeparation }) => {
  trackAction('21-526EZ_subtask_separationDateEntered', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName: 'rad',
    fieldName: 'separation-date',
    daysRemainingCategory: categorizeDaysRemaining(daysUntilSeparation),
  });
};

/**
 * Track claim type selection
 *
 * @param {string} claimType - Type of claim selected ('new-or-worsened' or 'disagree')
 */
export const trackClaimTypeSelection = claimType => {
  trackAction('21-526EZ_subtask_claimTypeSelected', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName: 'appeals',
    fieldName: 'claim-type',
    fieldValue: claimType,
  });
};

// ──────────────────────────────────────────────
// LINK CLICK TRACKING
// Track clicks on "File a claim" links at outcome pages
// ──────────────────────────────────────────────

/**
 * Track when user clicks the "File a disability claim" link
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.linkLabel - Text of the link clicked
 * @param {string} params.pageName - Name of the page where link was clicked
 * @param {Object} params.pathHistoryData - Path history object from context
 */
export const trackFileClaimLinkClick = ({
  linkLabel,
  pageName,
  pathHistoryData,
}) => {
  const history = pathHistoryData || {};

  trackAction('21-526EZ_subtask_fileClaimLinkClicked', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName,
    linkLabel,

    // Path history for context
    startTime: history.startTime,
    endTime: history.path?.[history.path.length - 1]?.timestamp,
    pathSteps: history.path?.map(p => p.step) || [],
    pathTimestamps: history.path?.map(p => p.timestamp) || [],
    totalSteps: history.path?.length || 0,
  });
};

/**
 * Track when user clicks a "Learn more" or informational link
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.linkLabel - Text of the link clicked
 * @param {string} params.pageName - Name of the page where link was clicked
 * @param {Object} params.pathHistoryData - Path history object from context
 */
export const trackExternalLinkClick = ({
  linkLabel,
  pageName,
  pathHistoryData,
}) => {
  const history = pathHistoryData || {};

  trackAction('21-526EZ_subtask_externalLinkClicked', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName,
    linkLabel,

    // Path history for context
    startTime: history.startTime,
    endTime: history.path?.[history.path.length - 1]?.timestamp,
    pathSteps: history.path?.map(p => p.step) || [],
    pathTimestamps: history.path?.map(p => p.timestamp) || [],
    totalSteps: history.path?.length || 0,
  });
};

// ──────────────────────────────────────────────
// OPT-OUT TRACKING
// (?) Track when users choose to skip subtask
// ──────────────────────────────────────────────

/**
 * Track when user opts out of the 526EZ form flow via the
 * "If you have a claim in progress, continue to your application" link on the
 * sub-task landing page
 *
 * @param {Object} params - Tracking parameters
 * @param {Object} params.pathHistoryData - Path history object from context
 */
export const trackOptOut = ({ pathHistoryData }) => {
  const history = pathHistoryData || {};

  trackAction('21-526EZ_subtask_userOptedOut', {
    formId: VA_FORM_IDS.FORM_21_526EZ,

    // Path history for context
    startTime: history.startTime,
    endTime: history.path?.[history.path.length - 1]?.timestamp,
    pathSteps: history.path?.map(p => p.step) || [],
    pathTimestamps: history.path?.map(p => p.timestamp) || [],
    totalSteps: history.path?.length || 0,
  });
};

// ──────────────────────────────────────────────
// ERROR TRACKING
// Track validation errors encountered
// ──────────────────────────────────────────────

/**
 * Track validation errors in subtask pages
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.pageName - Page where error occurred
 * @param {string} params.fieldName - Field that triggered the error
 * @param {string} params.errorMessage - Error message displayed to user
 */
export const trackSubtaskValidationError = ({
  pageName,
  fieldName,
  errorMessage,
}) => {
  trackAction('21-526EZ_subtask_validationError', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName,
    fieldName,
    errorType: 'validation',
    errorMessage,
  });
};

// ──────────────────────────────────────────────
// BDD-SPECIFIC TRACKING
// Track BDD eligibility information and warnings
// ──────────────────────────────────────────────

/**
 * Track when BDD eligibility message is displayed to user
 *
 * @param {Object} params - Tracking parameters
 * @param {number} params.daysRemaining - Number of days remaining to file BDD claim
 * @param {boolean} params.isLastDay - Whether this is the last day to file
 */
export const trackBddEligibilityDisplayed = ({ daysRemaining, isLastDay }) => {
  trackAction('21-526EZ_subtask_BDDEligibilityDisplayed', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    daysRemainingCategory: categorizeDaysRemaining(daysRemaining),
    isLastDay,
    eligibilityType: 'bdd',
  });
};

/**
 * Track when "unable to file BDD" message is displayed
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.reason - Reason user cannot file BDD ('too-early')
 * @param {number} params.daysRemaining - Number of days remaining until separation
 */
export const trackUnableToFileBddDisplayed = ({ reason, daysRemaining }) => {
  trackAction('21-526EZ_subtask_unableToFileBDDDisplayed', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    reason,
    daysRemainingCategory: categorizeDaysRemaining(daysRemaining),
    eligibilityType: 'not-eligible',
  });
};
